import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useRef,
} from "react";
import {
    PermissionService,
    UIFeaturePermissions,
} from "@/services/api/permission/PermissionService";
import { isApiError } from "@/services/BaseService";
import {
    hasRecoverableAuthHints,
    readAuthHintSnapshot,
} from "@/components/auth/authRecovery";
import logger from "@/utils/logger";

export interface PermissionContextType {
    permissions: string[];
    uiFeatures: UIFeaturePermissions | null;
    loading: boolean;
    error: string | null;
    hasPermission: (permissionCode: string) => boolean;
    hasAnyPermission: (permissionCodes: string[]) => boolean;
    hasAllPermissions: (permissionCodes: string[]) => boolean;
    canView: (feature: string) => boolean;
    canCreate: (feature: string) => boolean;
    canEdit: (feature: string) => boolean;
    canDelete: (feature: string) => boolean;
    refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

// 🔥 FIX: Global cache to prevent duplicate permission loads across component remounts
const permissionCache: {
    userId: number | null;
    permissions: string[];
    uiFeatures: UIFeaturePermissions | null;
    loadedAt: number;
} = {
    userId: null,
    permissions: [],
    uiFeatures: null,
    loadedAt: 0,
};

// 🔥 FIX: Global pending request map for deduplication
const pendingPermissionRequest: Map<number, Promise<void>> = new Map();

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;
// Recoverable permission loads can lag behind token/session restoration when
// many protected tabs initialize at once. Prefer keeping the display routes in
// a loading state longer instead of briefly rendering a false 403 screen.
const PERMISSION_LOAD_RETRY_DELAYS_MS = [300, 900, 2000, 4000, 8000, 12000];

interface PermissionProviderProps {
    children: ReactNode;
    userId: number | null;
}

const sleep = (ms: number) =>
    new Promise((resolve) => window.setTimeout(resolve, ms));

const hasRecoverablePermissionAuthHints = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }

    return hasRecoverableAuthHints(
        readAuthHintSnapshot(window.localStorage, false)
    );
};

const isRecoverablePermissionLoadError = (error: unknown): boolean => {
    if (
        error instanceof Error &&
        error.message &&
        error.message.includes("AUTH_EXPIRED")
    ) {
        return true;
    }

    if (isApiError(error) && (error.status === 401 || error.status === 403)) {
        return hasRecoverablePermissionAuthHints();
    }

    return false;
};

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
    children,
    userId,
}) => {
    const [permissions, setPermissions] = useState<string[]>([]);
    const [uiFeatures, setUiFeatures] = useState<UIFeaturePermissions | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Track component mount state
    const isMountedRef = useRef(true);

    const loadPermissions = React.useCallback(async () => {
        if (!userId) {
            setPermissions([]);
            setUiFeatures(null);
            setLoading(false);
            return;
        }

        // 🔥 FIX: Check cache first (synchronous check - must happen immediately)
        const now = Date.now();
        if (
            permissionCache.userId === userId &&
            permissionCache.loadedAt > 0 &&
            now - permissionCache.loadedAt < CACHE_DURATION
        ) {
            logger.info("✅ Using cached permissions for user:", userId);
            setPermissions(permissionCache.permissions);
            setUiFeatures(permissionCache.uiFeatures);
            setLoading(false);
            return;
        }

        // 🔥 FIX: Check if request is already in progress (global deduplication)
        const pendingRequest = pendingPermissionRequest.get(userId);
        if (pendingRequest) {
            logger.info(
                "🔄 Reusing pending permission request for user:",
                userId
            );
            try {
                await pendingRequest;
                // After pending request completes, cache should be populated
                if (
                    permissionCache.userId === userId &&
                    permissionCache.loadedAt > 0 &&
                    isMountedRef.current
                ) {
                    setPermissions(permissionCache.permissions);
                    setUiFeatures(permissionCache.uiFeatures);
                    setLoading(false);
                }
            } catch {
                // Error already handled by original request
            }
            return;
        }

        // Create new request promise
        const requestPromise = (async () => {
            try {
                logger.info("🔄 Loading permissions for user:", userId);
                setLoading(true);
                setError(null);

                let userUIFeatures: UIFeaturePermissions | null = null;

                for (
                    let attempt = 0;
                    attempt <= PERMISSION_LOAD_RETRY_DELAYS_MS.length;
                    attempt++
                ) {
                    try {
                        // 🔥 FIX: Only load UI features (it contains rawPermissions already)
                        userUIFeatures =
                            await PermissionService.getUserUIFeatures(userId);
                        break;
                    } catch (error) {
                        const shouldRetry =
                            attempt < PERMISSION_LOAD_RETRY_DELAYS_MS.length &&
                            isRecoverablePermissionLoadError(error);

                        if (!shouldRetry) {
                            throw error;
                        }

                        logger.info(
                            "🔄 Retrying permission load after recoverable auth error",
                            {
                                userId,
                                attempt: attempt + 1,
                            }
                        );

                        await sleep(PERMISSION_LOAD_RETRY_DELAYS_MS[attempt]);
                    }
                }

                if (!userUIFeatures) {
                    throw new Error("Permission load completed without data");
                }

                // Use rawPermissions from UI features instead of separate call
                const permissionsList = userUIFeatures.rawPermissions || [];

                // 🔥 FIX: Update cache
                permissionCache.userId = userId;
                permissionCache.permissions = permissionsList;
                permissionCache.uiFeatures = userUIFeatures;
                permissionCache.loadedAt = Date.now();

                if (isMountedRef.current) {
                    setPermissions(permissionsList);
                    setUiFeatures(userUIFeatures);
                    logger.info(
                        "✅ Permissions loaded:",
                        permissionsList.length,
                        "permissions"
                    );
                }
            } catch (err) {
                console.error("Failed to load permissions:", err);
                if (isMountedRef.current) {
                    setError("Failed to load permissions");
                    setPermissions([]);
                    setUiFeatures(null);
                }
                throw err;
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
                pendingPermissionRequest.delete(userId);
            }
        })();

        pendingPermissionRequest.set(userId, requestPromise);
        await requestPromise;
    }, [userId]);

    useEffect(() => {
        isMountedRef.current = true;
        void loadPermissions().catch(() => undefined);
        return () => {
            isMountedRef.current = false;
        };
    }, [loadPermissions]);

    const hasPermission = (permissionCode: string): boolean => {
        return permissions.includes(permissionCode);
    };

    const hasAnyPermission = (permissionCodes: string[]): boolean => {
        return permissionCodes.some((code) => permissions.includes(code));
    };

    const hasAllPermissions = (permissionCodes: string[]): boolean => {
        return permissionCodes.every((code) => permissions.includes(code));
    };

    const canView = (feature: string): boolean => {
        if (!uiFeatures?.features[feature]) return false;
        const featurePermissions = uiFeatures.features[feature] as {
            canView?: boolean;
            canCreate?: boolean;
            canEdit?: boolean;
            canDelete?: boolean;
        };
        return featurePermissions.canView === true;
    };

    const canCreate = (feature: string): boolean => {
        if (!uiFeatures?.features[feature]) return false;
        const featurePermissions = uiFeatures.features[feature] as {
            canView?: boolean;
            canCreate?: boolean;
            canEdit?: boolean;
            canDelete?: boolean;
        };
        return featurePermissions.canCreate === true;
    };

    const canEdit = (feature: string): boolean => {
        if (!uiFeatures?.features[feature]) return false;
        const featurePermissions = uiFeatures.features[feature] as {
            canView?: boolean;
            canCreate?: boolean;
            canEdit?: boolean;
            canDelete?: boolean;
        };
        return featurePermissions.canEdit === true;
    };

    const canDelete = (feature: string): boolean => {
        if (!uiFeatures?.features[feature]) return false;
        const featurePermissions = uiFeatures.features[feature] as {
            canView?: boolean;
            canCreate?: boolean;
            canEdit?: boolean;
            canDelete?: boolean;
        };
        return featurePermissions.canDelete === true;
    };

    const refreshPermissions = async () => {
        // 🔥 FIX: Clear cache before refreshing
        permissionCache.loadedAt = 0;
        // Clear any pending request so a fresh one is made
        if (userId) {
            pendingPermissionRequest.delete(userId);
        }
        await loadPermissions();
    };

    const value: PermissionContextType = {
        permissions,
        uiFeatures,
        loading,
        error,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        canView,
        canCreate,
        canEdit,
        canDelete,
        refreshPermissions,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = (): PermissionContextType => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error(
            "usePermissions must be used within a PermissionProvider"
        );
    }
    return context;
};

export const useOptionalPermissions = (): PermissionContextType | null => {
    return useContext(PermissionContext);
};
